import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Send, CreditCard, User, FileText, Calendar, DollarSign, AlertCircle } from 'lucide-react';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { useToast } from '@/hooks/useToast';
import { useFormValidation, validation } from '@/utils/validation';
import { Card, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';

interface InvoiceForm {
  customerName: string;
  customerEmail: string;
  jobTitle: string;
  description: string;
  amount: string;
  dueDate: string;
  notes: string;
}

export function CreateInvoicePage() {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data,
    errors,
    touched,
    updateField,
    touchField,
    validateAll,
    isValid
  } = useFormValidation<InvoiceForm>(
    {
      customerName: '',
      customerEmail: '',
      jobTitle: '',
      description: '',
      amount: '',
      dueDate: '',
      notes: ''
    },
    {
      customerName: (value) => validation.required(value, 'Customer name'),
      customerEmail: validation.email,
      jobTitle: (value) => validation.required(value, 'Job title'),
      description: (value) => validation.textLength(value, 10, 500, 'Description'),
      amount: (value) => {
        if (!value) return 'Amount is required';
        const num = parseFloat(value);
        if (isNaN(num) || num <= 0) return 'Amount must be a positive number';
        return null;
      },
      dueDate: (value) => validation.required(value, 'Due date'),
      notes: () => null
    }
  );

  const handleSubmit = async (e: React.FormEvent, action: 'save' | 'send') => {
    e.preventDefault();

    if (!validateAll()) {
      error('Please fix the errors before submitting');
      return;
    }

    setIsSubmitting(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));

      const invoiceNumber = `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-3)}`;

      if (action === 'save') {
        success(`Invoice ${invoiceNumber} saved as draft`);
      } else {
        success(`Invoice ${invoiceNumber} sent to ${data.customerName}`);
      }

      navigate('/invoices');
    } catch (err) {
      error('Failed to create invoice. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb items={[{ label: 'Invoices', href: '/invoices' }, { label: 'Create Invoice' }]} />

        <div className="mb-6 mt-4 flex items-center gap-3">
          <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
            <CreditCard className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create New Invoice</h1>
            <p className="text-gray-600 text-sm">Generate and modify invoices for your clients.</p>
          </div>
        </div>

        <form onSubmit={(e) => e.preventDefault()} className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <Card>
            <CardHeader title="Client Information" icon={<User className="w-5 h-5 text-gray-400" />} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={data.customerName}
                  onChange={(e) => updateField('customerName', e.target.value)}
                  onBlur={() => touchField('customerName')}
                  placeholder="e.g. John Doe"
                  className={`w-full form-input rounded-md shadow-sm text-sm ${touched.customerName && errors.customerName ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'}`}
                />
                {touched.customerName && errors.customerName && <p className="mt-1 text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {errors.customerName}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Name <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  value={data.customerEmail}
                  onChange={(e) => updateField('customerEmail', e.target.value)}
                  onBlur={() => touchField('customerEmail')}
                  placeholder="e.g. john@example.com"
                  className={`w-full form-input rounded-md shadow-sm text-sm ${touched.customerEmail && errors.customerEmail ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'}`}
                />
                {touched.customerEmail && errors.customerEmail && <p className="mt-1 text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {errors.customerEmail}</p>}
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader title="Invoice Details" icon={<FileText className="w-5 h-5 text-gray-400" />} />
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Title / Reference <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={data.jobTitle}
                  onChange={(e) => updateField('jobTitle', e.target.value)}
                  onBlur={() => touchField('jobTitle')}
                  placeholder="e.g. Kitchen Renovation"
                  className={`w-full form-input rounded-md shadow-sm text-sm ${touched.jobTitle && errors.jobTitle ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'}`}
                />
                {touched.jobTitle && errors.jobTitle && <p className="mt-1 text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {errors.jobTitle}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description <span className="text-red-500">*</span></label>
                <textarea
                  value={data.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  onBlur={() => touchField('description')}
                  rows={4}
                  placeholder="Enter detailed description of services..."
                  className={`w-full form-textarea rounded-md shadow-sm text-sm ${touched.description && errors.description ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'}`}
                />
                {touched.description && errors.description && <p className="mt-1 text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {errors.description}</p>}
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader title="Payment Information" icon={<DollarSign className="w-5 h-5 text-gray-400" />} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (€) <span className="text-red-500">*</span></label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">€</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    value={data.amount}
                    onChange={(e) => updateField('amount', e.target.value)}
                    onBlur={() => touchField('amount')}
                    className={`w-full pl-7 form-input rounded-md shadow-sm text-sm ${touched.amount && errors.amount ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'}`}
                    placeholder="0.00"
                  />
                </div>
                {touched.amount && errors.amount && <p className="mt-1 text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {errors.amount}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={data.dueDate}
                  onChange={(e) => updateField('dueDate', e.target.value)}
                  onBlur={() => touchField('dueDate')}
                  className={`w-full form-input rounded-md shadow-sm text-sm ${touched.dueDate && errors.dueDate ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'}`}
                />
                {touched.dueDate && errors.dueDate && <p className="mt-1 text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {errors.dueDate}</p>}
              </div>
            </div>
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
              <textarea
                value={data.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                rows={2}
                placeholder="Additional notes for the invoice..."
                className="w-full form-textarea rounded-md border-gray-300 shadow-sm text-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          </Card>

          <div className="flex flex-col sm:flex-row justify-end gap-4 pt-4">
            <Button variant="ghost" onClick={() => navigate('/invoices')} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="outline" onClick={() => handleSubmit(new Event('click') as any, 'save')} disabled={isSubmitting} icon={<Save className="w-4 h-4" />}>
              Save Draft
            </Button>
            <Button onClick={() => handleSubmit(new Event('click') as any, 'send')} disabled={isSubmitting || !isValid} icon={<Send className="w-4 h-4" />}>
              {isSubmitting ? 'Sending...' : 'Send Invoice'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}